import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { showToast } from "@/components/shared/Toast";
import { Star, Clock } from "lucide-react";

const EMOJIS = ["😞", "😐", "🙂", "😄", "🤩"] as const;
type Emoji = typeof EMOJIS[number];

interface Review {
  emoji?: string;
  encrypted_feedback?: string;
  nonce?: string;
  submitted_at?: string;
}

interface SessionPostmortemProps {
  ownReview: Review | null;
  partnerReview: Review | null;
  ownRole: "admin" | "client";
  encryptValue: (value: string) => Promise<string>;
  decryptValue: (value: string) => Promise<string>;
  hasKey: boolean;
  onSubmit: (emoji: string, encrypted_feedback: string) => Promise<void>;
}

export function SessionPostmortem({
  ownReview,
  partnerReview,
  ownRole,
  encryptValue,
  decryptValue,
  hasKey,
  onSubmit,
}: SessionPostmortemProps) {
  const [selectedEmoji, setSelectedEmoji] = useState<Emoji | null>(null);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ownFeedbackDecrypted, setOwnFeedbackDecrypted] = useState<string | null>(null);
  const [partnerFeedbackDecrypted, setPartnerFeedbackDecrypted] = useState<string | null>(null);

  const alreadySubmitted = !!ownReview?.submitted_at;
  const partnerSubmitted = !!partnerReview?.submitted_at;
  // Full partner reveal: only if both submitted (partner object includes encrypted_feedback)
  const partnerFullyRevealed = partnerSubmitted && !!partnerReview?.emoji;

  // Decrypt own feedback
  useEffect(() => {
    if (!ownReview?.encrypted_feedback || !hasKey) return;
    decryptValue(ownReview.encrypted_feedback).then(setOwnFeedbackDecrypted);
  }, [ownReview, hasKey, decryptValue]);

  // Decrypt partner feedback (only available when both have submitted)
  useEffect(() => {
    if (!partnerReview?.encrypted_feedback || !partnerFullyRevealed || !hasKey) return;
    decryptValue(partnerReview.encrypted_feedback).then(setPartnerFeedbackDecrypted);
  }, [partnerReview, partnerFullyRevealed, hasKey, decryptValue]);

  const handleSubmit = async () => {
    if (!selectedEmoji || submitting) return;
    setSubmitting(true);
    try {
      const encrypted = feedback.trim()
        ? await encryptValue(feedback.trim())
        : "";
      await onSubmit(selectedEmoji, encrypted);
    } catch {
      showToast.error("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const partnerLabel = ownRole === "admin" ? "Partner" : "Admin";

  return (
    <Card className="border-app-border rounded-2xl overflow-hidden pt-0">
      <CardHeader className="pb-2 bg-app-accent/5 rounded-t-2xl pt-4">
        <CardTitle className="text-body flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-app-accent/15 flex items-center justify-center">
            <Star className="w-4 h-4 text-app-accent" />
          </div>
          <span>Postmortem</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Status */}
        {alreadySubmitted && !partnerFullyRevealed && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-app-info/10 border border-app-info/20">
            <Clock className="w-4 h-4 text-app-info shrink-0" />
            <p className="text-info text-app-text-muted">
              {partnerSubmitted
                ? "Loading partner's review…"
                : `Waiting for ${partnerLabel} to submit their review.`}
            </p>
          </div>
        )}

        {/* Both submitted — reveal cards */}
        {partnerFullyRevealed && (
          <div className="grid grid-cols-2 gap-3">
            {/* Own */}
            <div className="p-3 rounded-xl bg-app-surface-raised border border-app-border space-y-2">
              <p className="text-info font-semibold text-app-text-muted">Your review</p>
              <p className="text-3xl text-center">{ownReview?.emoji}</p>
              {ownFeedbackDecrypted && (
                <MarkdownText text={ownFeedbackDecrypted} className="text-info text-app-text-muted" />
              )}
            </div>
            {/* Partner */}
            <div className="p-3 rounded-xl bg-app-primary/5 border border-app-primary/20 space-y-2">
              <p className="text-info font-semibold text-app-text-muted">{partnerLabel}'s review</p>
              <p className="text-3xl text-center">{partnerReview?.emoji}</p>
              {partnerFeedbackDecrypted && (
                <MarkdownText text={partnerFeedbackDecrypted} className="text-info text-app-text-muted" />
              )}
            </div>
          </div>
        )}

        {/* Own already submitted (awaiting partner) — show own review */}
        {alreadySubmitted && !partnerFullyRevealed && ownReview && (
          <div className="p-3 rounded-xl bg-app-surface-raised border border-app-border space-y-2">
            <p className="text-info font-semibold text-app-text-muted">Your review</p>
            <p className="text-3xl text-center">{ownReview.emoji}</p>
            {ownFeedbackDecrypted && (
              <MarkdownText text={ownFeedbackDecrypted} className="text-info text-app-text-muted" />
            )}
          </div>
        )}

        {/* Submit form — only if not yet submitted */}
        {!alreadySubmitted && (
          <div className="space-y-3">
            <p className="text-info font-medium text-app-text-muted">
              How did the session go?
            </p>

            {/* Emoji picker */}
            <div className="flex justify-center gap-2">
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setSelectedEmoji(e)}
                  className={`text-3xl p-2 rounded-xl transition-all ${
                    selectedEmoji === e
                      ? "bg-app-primary/15 scale-110 ring-2 ring-app-primary/40"
                      : "hover:bg-app-surface-raised hover:scale-105"
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>

            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Optional notes (Markdown supported, encrypted)…"
              rows={3}
            />

            <Button
              onClick={handleSubmit}
              disabled={!selectedEmoji || submitting}
              className="w-full bg-app-primary hover:bg-app-primary-hover"
            >
              {submitting ? "Submitting…" : "Submit Review"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
